<script lang="ts">
  import type {IGame} from "$lib/interfaces/IGame";
  import GameCard from "$lib/components/GameCard.svelte";
  import Button from "$lib/elements/Button.svelte";
  import type {IProvider} from "$lib/interfaces/IProvider";
  import {DetectedCountry, OverlayLoaderShow, user} from "$lib/stores";
  import {locale, translate} from "$lib/i18n";
  import {goto} from "$app/navigation";
  import VipProgram from "$lib/components/VipProgram.svelte";
  import MobilePWA from "$lib/components/MobilePWA.svelte";
  import WeeklyGallopTournament from "$lib/components/WeeklyGallopTournament.svelte";
  import AboutUs from "$lib/components/AboutUs.svelte";
  import {onMount, onDestroy} from "svelte";
  import {openDialog} from "$lib/stores/dialogStores";
  import {page} from "$app/stores";
  import { get } from 'svelte/store';
  import { browser } from '$app/environment';
  import { derived } from 'svelte/store';

  interface Props {
    data: any;
  }

  let { data }: Props = $props();
  let redirectAttempted = false;

  // Safely access games with fallback to empty array
  let games: IGame[] = $state(data?.props?.data1?.data || []);

  // Set detected country if available
  if (data?.props?.data2?.data?.countryCode) {
    DetectedCountry.set(data.props.data2.data.countryCode);
  }

  let activeButton = $state($page.url.searchParams.get('crypto') ? 'crypto' : 'fiat');

  // Improved redirect logic with error handling
  onMount(() => {
    // Only run in browser environment
    if (!browser) return;

    try {
      // Check if token exists and is valid
      if (data?.token && !redirectAttempted) {
        redirectAttempted = true;

        // Show loading indicator during redirect
        OverlayLoaderShow.set(true);

        // Add a small delay to ensure stores are properly initialized
        setTimeout(() => {
          goto(`/${get(locale)}/casino`)
                  .then(() => {
                    OverlayLoaderShow.set(false);
                  })
                  .catch(error => {
                    // If navigation fails, try to recover with direct navigation
                    window.location.href = `/${get(locale)}/casino`;
                  });
        }, 100);
      } else {
        // No token or redirect already attempted, ensure loader is hidden
        OverlayLoaderShow.set(false);
      }
    } catch (error) {
      // Ensure loader is hidden on error
      OverlayLoaderShow.set(false);

      // If there's an error in the redirect logic, don't break the page
      // Just log the error and let the user stay on the home page
    }
  });

  function openRegister() {
    openDialog('RegistrationModal');
  }

  function switchContent(option:string) {
    activeButton = option;
  }
</script>

<svelte:head>
  <title>{$translate('title')}</title>
  <meta name="description" content="Forza.Bet - Online Casino" />
  <!-- Add cache control meta tags -->
  <meta http-equiv="Cache-Control" content="no-cache, no-store, must-revalidate" />
  <meta http-equiv="Pragma" content="no-cache" />
  <meta http-equiv="Expires" content="0" />
</svelte:head>

<div class="flex lg:hidden justify-center relative">
  {#if activeButton === 'fiat'}
    <img src="/main-banner/main-banner-mobile.png" class="w-full" alt="Forza.bet - 100% cashback" />
  {:else if activeButton === 'crypto'}
    <img src="/main-banner/main-banner-crypto.png" class="w-full" alt="Forza.bet - logo" />
  {/if}
  <div class="absolute bottom-16 left-1/2 transform -translate-x-1/2 flex flex-col gap-2 items-center w-full mt-12">
    {#if activeButton === 'fiat'}
      <h1 class="text-white font-bold drop-shadow-2xl gradient-text-shadow">Exclusive Welcome Offer</h1>
      <h2 class="gradient-text-fire text-2xl font-bold gradient-text-shadow">200% up to 1500 £/€/$</h2>
      <h3 class="text-white text-sm font-bold drop-shadow-2xl gradient-text-shadow">on Your First Three Deposits!</h3>
    {:else if activeButton === 'crypto'}
      <h1 class="text-white font-bold drop-shadow-2xl gradient-text-shadow">CRYPTO Welcome BONUS</h1>
      <h2 class="gradient-text-fire text-2xl font-bold gradient-text-shadow">200% Match up to 0.5 BTC!</h2>
      <h3 class="text-white text-sm font-bold drop-shadow-2xl gradient-text-shadow">on Your First Three Deposits!</h3>
    {/if}

    <button class="fire-btn rounded-full" onclick={() => openRegister()}>
      <div class="flex flex-col items-center px-6 py-3 text-sm md:text-2xl ">
        <span>{$translate('Quick Sign-Up')}</span>
        <span class="text-xs">{$translate('Only Takes a Minute')}</span>
      </div>
    </button>

  </div>
  <div class="absolute bottom-1 lg:bottom-4 left-1/2 transform -translate-x-1/2 flex gap-3 bg-black/60 p-2 rounded-full shadow-lg">
    <button type="button" class="px-5 py-1.5 text-sm font-medium rounded-full text-center transition-all duration-300 border {activeButton === 'fiat' ? 'bg-primary-fire/80 text-white border-primary-fire' : 'text-primary-fire border-primary-fire hover:bg-black/50 hover:text-white'}" onclick={() => switchContent('fiat')}>Money</button>
    <button type="button" class="px-5 py-1.5 text-sm font-medium rounded-full text-center transition-all duration-300 border {activeButton === 'crypto' ? 'bg-primary-fire/80 text-white border-primary-fire' : 'text-primary-fire border-primary-fire hover:bg-black/50 hover:text-white'}" onclick={() => switchContent('crypto')}>Crypto</button>
  </div>
</div>


<div class="hidden lg:block relative main-banner lg:min-h-[350px] xl:min-h-[430px]">
  {#if activeButton === 'fiat'}
    <img src="/main-banner/img_1.png" class="w-full" alt="Forza.bet - logo" />
    <img src="/main-banner/horsee.avifs" class="w-[53%] hidden xl:block absolute -left-4 -top-6" alt="Forza.bet - horse">
    <img src="/main-banner/horse.png" class="w-[53%] block xl:hidden absolute -left-12 -top-6" alt="Forza.bet - horse">

  {:else if activeButton === 'crypto'}
    <img src="/main-banner/background-crypto.png" class="w-full" alt="Forza.bet - logo"/>
    <img src="/main-banner/Forza.avifs" class="w-[60%] hidden xl:block absolute -left-4 -top-6" alt="Forza.bet - horse">
    <img src="/main-banner/hors.png" class="w-[60%] block xl:hidden absolute -left-12 -top-6" alt="Forza.bet - horse">
  {/if}
  <div class="absolute left-0 right-0 bottom-0 top-32" style="background: linear-gradient(0deg, black 40px, transparent);"></div>

  <div class="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 flex flex-col gap-4 items-center">
    {#if activeButton === 'fiat'}
      <h1 class="text-white text-2xl font-bold drop-shadow-2xl gradient-text-shadow">Exclusive Welcome Offer</h1>
      <h2 class="gradient-text-fire text-5xl font-bold gradient-text-shadow text-nowrap">200% up to 1500 £/€/$</h2>
      <h3 class="text-white text-xl font-bold drop-shadow-2xl gradient-text-shadow">on Your First Three Deposits!</h3>
    {:else if activeButton === 'crypto'}
      <h1 class="text-white text-2xl font-bold drop-shadow-2xl gradient-text-shadow">CRYPTO Welcome BONUS</h1>
      <h2 class="gradient-text-fire text-5xl font-bold gradient-text-shadow text-nowrap">200% Match up to 0.5 BTC!</h2>
      <h3 class="text-white text-xl font-bold drop-shadow-2xl gradient-text-shadow">on Your First Three Deposits!</h3>
    {/if}
    <button class="fire-btn rounded-full" onclick={() => openRegister()}>
      <div class="flex flex-col items-center md:px-14 md:py-5">
        <span class="text-lg">{$translate('Quick Sign-Up')}</span>
        <span class="text-xs">{$translate('Only Takes a Minute')}</span>
      </div>
    </button>
  </div>

  <div class="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex gap-3 bg-black/60 p-2 rounded-full shadow-lg">
    <button type="button" class="px-5 py-1.5 text-sm font-medium rounded-full text-center transition-all duration-300 border {activeButton === 'fiat' ? 'bg-primary-fire/80 text-white border-primary-fire' : 'text-primary-fire border-primary-fire hover:bg-black/50 hover:text-white'}" onclick={() => switchContent('fiat')}>Money</button>
    <button type="button" class="px-5 py-1.5 text-sm font-medium rounded-full text-center transition-all duration-300 border {activeButton === 'crypto' ? 'bg-primary-fire/80 text-white border-primary-fire' : 'text-primary-fire border-primary-fire hover:bg-black/50 hover:text-white'}" onclick={() => switchContent('crypto')}>Crypto</button>
  </div>
</div>

<div class="my-11 text-center">
  <h2 class="text-center text-[#F93C2C] text-[32px] font-bold pb-11">{$translate('Popular Games')}</h2>
  <div class="grid grid-cols-3 md:grid-cols-6 flex-wrap justify-center gap-2 md:gap-8 mb-11">
    {#each games as game}
      {#if game && game.image}
        <GameCard {game} />
      {/if}
    {/each}
  </div>
  <Button on:click={() => goto(`/${get(locale)}/casino`)}>{$translate('Play 500+ Games')}</Button>
</div>

<VipProgram />

<WeeklyGallopTournament />

<MobilePWA />

<AboutUs />

<style lang="scss">
  .gradient-text-shadow {
    filter: drop-shadow(0px 4px 4px rgba(0, 0, 0, 0.5));
  }

  @keyframes pulse {
    0% {
      -webkit-box-shadow: #f93b2b 0 0 0 0;
      box-shadow: #f93b2b 0 0 0 0;
    }

    100% {
      -webkit-box-shadow: rgba(150, 92, 245, 0) 0 0 0 20px;
      box-shadow: rgba(150, 92, 245, 0) 0 0 0 20px;
    }
  }

  .fire-btn {
    color: white;
    animation: 1.5s ease 0s infinite normal none running pulse;
    border: none;
    cursor: pointer;
  }
</style>